import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';
import type { ProfitAndLoss, IncomeStatementAccount } from '@/types';


export function ProfitLoss() {
  const { idToken } = useCognitoAuth();
  const [report, setReport] = useState<ProfitAndLoss | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (!idToken) return;
    async function fetchReport() {
      if (!startDate || !endDate) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await api.get<ProfitAndLoss>(`/api/reports/income-statement?start_date=${startDate}&end_date=${endDate}`, { errorCode: ErrorCode.FETCH_PROFIT_LOSS, token: idToken! });
        setReport(data);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch profit & loss');
      } finally {
        setLoading(false);
      }
    }
    void fetchReport();
  }, [startDate, endDate, idToken]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Math.abs(amount));
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div className="flex justify-between items-center pb-4 border-b border-border">
          <div>
            <div className="h-8 w-36 bg-muted rounded mb-2" />
            <div className="h-4 w-28 bg-muted rounded" />
          </div>
          <div className="flex gap-2">
            <div className="h-8 w-28 bg-muted rounded-md" />
            <div className="h-8 w-16 bg-muted rounded-md" />
          </div>
        </div>
        <div className="bg-card rounded-lg border border-border p-6 space-y-3">
          <div className="h-5 w-24 bg-muted rounded" />
          {[1,2,3].map(i => (
            <div key={i} className="flex justify-between py-2">
              <div className="h-4 w-36 bg-muted rounded" />
              <div className="h-4 w-20 bg-muted rounded" />
            </div>
          ))}
          <div className="flex justify-between pt-2 border-t border-border">
            <div className="h-4 w-24 bg-muted rounded" />
            <div className="h-5 w-24 bg-muted rounded" />
          </div>
        </div>
        <div className="bg-card rounded-lg border border-border p-6 space-y-3">
          <div className="h-5 w-24 bg-muted rounded" />
          {[1,2,3].map(i => (
            <div key={i} className="flex justify-between py-2">
              <div className="h-4 w-40 bg-muted rounded" />
              <div className="h-4 w-24 bg-muted rounded" />
            </div>
          ))}
          <div className="flex justify-between pt-2 border-t border-border">
            <div className="h-4 w-28 bg-muted rounded" />
            <div className="h-5 w-24 bg-muted rounded" />
          </div>
        </div>
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex justify-between items-center">
            <div className="space-y-2">
              <div className="h-5 w-20 bg-muted rounded" />
              <div className="h-4 w-40 bg-muted rounded" />
            </div>
            <div className="h-8 w-24 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <i className="bi bi-exclamation-triangle text-3xl text-red-600 mb-4"></i>
        <p className="text-muted-foreground">Error: {error}</p>
      </div>
    );
  }

  const revenue = report?.revenue ?? { accounts: [], total: 0 };
  const expenses = report?.expenses ?? { accounts: [], total: 0 };
  const netIncome = report?.net_income ?? 0;
  const isProfit = report?.is_profit ?? Number(netIncome) >= 0;

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Profit & Loss</h1>
          <p className="text-sm text-muted-foreground">Income Statement</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start date"
          />
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End date"
          />
          <Button variant="outline" size="sm">Export CSV</Button>
          <Button variant="default" size="sm">Print</Button>
        </div>
      </div>

      {startDate && endDate && report && (
        <p className="text-sm text-muted-foreground">
          For the period {new Date(startDate).toLocaleDateString('en-CA')} to {new Date(endDate).toLocaleDateString('en-CA')}
        </p>
      )}

      {/* Revenue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-arrow-down-circle"></i> Revenue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                  <th className="py-2 px-3 text-right font-medium text-muted-foreground">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(revenue.accounts ?? []).map((account: IncomeStatementAccount) => (
                  <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-2 px-3">
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
                      {account.name}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-green-600">
                      {formatCurrency(Number(account.balance))}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold bg-muted/50">
                  <td className="py-2 px-3">Total Revenue</td>
                  <td className="py-2 px-3 text-right font-mono text-success">{formatCurrency(Number(revenue.total))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Expenses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-arrow-up-circle"></i> Expenses
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                  <th className="py-2 px-3 text-right font-medium text-muted-foreground">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(expenses.accounts ?? []).map((account: IncomeStatementAccount) => (
                  <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-2 px-3">
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
                      {account.name}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-red-600">
                      {formatCurrency(Number(account.balance))}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold bg-muted/50">
                  <td className="py-2 px-3">Total Expenses</td>
                  <td className="py-2 px-3 text-right font-mono text-danger">{formatCurrency(Number(expenses.total))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Net Income */}
      <Card className={isProfit ? 'border-primary/30 bg-primary/5' : 'border-red-300 bg-red-50/30'}>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Net {isProfit ? 'Income' : 'Loss'}</h3>
              <p className="text-sm text-muted-foreground">Total Revenue - Total Expenses</p>
            </div>
            <div className={`text-2xl font-bold font-mono ${isProfit ? 'text-success' : 'text-danger'}`}>
              {isProfit ? '+' : '-'}{formatCurrency(Number(netIncome))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
